import { useState, useRef, useEffect } from 'react'
import PhoneInput from 'react-phone-number-input'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { Input, Select, Button } from '../common/UI'
import { OPTIONS } from '../../utils/constants'
import { validators } from '../../utils/validation'
import { MapPin, Search, X, ShieldAlert } from 'lucide-react'
import { businessService } from '../../services/api'
import 'react-phone-number-input/style.css'

interface BusinessFormProps {
  business?: any
  onSubmit: (data: any) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

const GOOGLE_API_KEY = 'AIzaSyDfupwvoZMJ51lO5ASk8rvI4JPqyUFaiaE'

declare global {
  interface Window {
    google?: any
  }
}

export const BusinessForm: React.FC<BusinessFormProps> = ({
  business,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    businessName: business?.businessName || '',
    businessEmail: business?.businessEmail || '',
    contactPerson: business?.contactPerson || '',
    phoneNumber: business?.phoneNumber || '',
    businessType: business?.businessType || '',
    website: business?.website || '',
    address: business?.address?.address || '',
    lat: business?.address?.lat || null,
    long: business?.address?.long || null
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [emailVerified, setEmailVerified] = useState(!!business?.emailVerified)
  const [phoneVerified, setPhoneVerified] = useState(!!business?.phoneVerified)
  const [searchingLocation, setSearchingLocation] = useState(false)
  const [locationSuggestions, setLocationSuggestions] = useState<any[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [googleLoaded, setGoogleLoaded] = useState(false)
  const searchTimeoutRef = useRef<number | null>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const autocompleteServiceRef = useRef<any>(null)
  const placesServiceRef = useRef<any>(null)

  const [emailOtpSent, setEmailOtpSent] = useState(false)
  const [phoneOtpSent, setPhoneOtpSent] = useState(false)
  const [emailOtp, setEmailOtp] = useState('')
  const [phoneOtp, setPhoneOtp] = useState('')
  const [emailCountdown, setEmailCountdown] = useState(0)
  const [phoneCountdown, setPhoneCountdown] = useState(0)
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [verifyingEmail, setVerifyingEmail] = useState(false)
  const [verifyingPhone, setVerifyingPhone] = useState(false)

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google?.maps?.places) {
        if (!autocompleteServiceRef.current) {
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
          const dummyDiv = document.createElement('div')
          placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv)
          setGoogleLoaded(true)
        }
        return
      }

      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        const checkInterval = setInterval(() => {
          if (window.google?.maps?.places) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
            const dummyDiv = document.createElement('div')
            placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv)
            setGoogleLoaded(true)
            clearInterval(checkInterval)
          }
        }, 100)
        setTimeout(() => clearInterval(checkInterval), 10000)
        return
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
      script.async = true
      script.defer = true
      
      script.onload = () => {
        const checkInterval = setInterval(() => {
          if (window.google?.maps?.places) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
            const dummyDiv = document.createElement('div')
            placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv)
            setGoogleLoaded(true)
            clearInterval(checkInterval)
          }
        }, 100)
        setTimeout(() => clearInterval(checkInterval), 10000)
      }

      script.onerror = (error) => {
        console.error('Failed to load Google Maps:', error)
      }

      document.head.appendChild(script)
    }

    loadGoogleMaps()
  }, [])

  useEffect(() => {
    if (emailCountdown > 0) {
      const timer = setTimeout(() => setEmailCountdown(emailCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [emailCountdown])

  useEffect(() => {
    if (phoneCountdown > 0) {
      const timer = setTimeout(() => setPhoneCountdown(phoneCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [phoneCountdown])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required'
    } else if (formData.businessName.length < 2) {
      newErrors.businessName = 'Business name must be at least 2 characters'
    } else if (formData.businessName.length > 100) {
      newErrors.businessName = 'Business name must be less than 100 characters'
    }

    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = 'Business email is required'
    } else if (!validators.email(formData.businessEmail)) {
      newErrors.businessEmail = 'Invalid email address'
    }

    if (!emailVerified) {
      newErrors.businessEmail = 'Please verify business email'
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person name is required'
    } else if (formData.contactPerson.length < 2) {
      newErrors.contactPerson = 'Contact person name must be at least 2 characters'
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required'
    } else if (!isValidPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Invalid phone number'
    }

    if (!phoneVerified) {
      newErrors.phoneNumber = 'Please verify phone number'
    }

    if (!formData.businessType) {
      newErrors.businessType = 'Business type is required'
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Business address is required'
    } else if (formData.address.length < 10) {
      newErrors.address = 'Address must be at least 10 characters'
    } else if (formData.address.length > 500) {
      newErrors.address = 'Address must be less than 500 characters'
    }

    if (formData.website && !validators.url(formData.website)) {
      newErrors.website = 'Invalid website URL (must start with http:// or https://)'
    }

    return newErrors
  }

  const handleAddressSearch = async (searchTerm: string) => {
    if (!googleLoaded || !autocompleteServiceRef.current || !searchTerm || searchTerm.length < 3) {
      setLocationSuggestions([])
      setShowSuggestions(false)
      return
    }

    setSearchingLocation(true)
    try {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: searchTerm,
          types: ['address']
        },
        (predictions: any[], status: string) => {
          if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && predictions && predictions.length > 0) {
            setLocationSuggestions(predictions)
            setShowSuggestions(true)
          } else {
            setLocationSuggestions([])
            setShowSuggestions(false)
          }
          setSearchingLocation(false)
        }
      )
    } catch (error) {
      console.error('Location search failed:', error)
      setLocationSuggestions([])
      setShowSuggestions(false)
      setSearchingLocation(false)
    }
  }

  const getPlaceDetails = async (placeId: string) => {
    if (!placesServiceRef.current) return null

    return new Promise((resolve) => {
      placesServiceRef.current.getDetails(
        {
          placeId: placeId,
          fields: ['formatted_address', 'geometry']
        },
        (place: any, status: string) => {
          if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && place) {
            resolve({
              address: place.formatted_address,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            })
          } else {
            resolve(null)
          }
        }
      )
    })
  }

  const handleAddressSelect = async (prediction: any) => {
    const details: any = await getPlaceDetails(prediction.place_id)
    
    if (details) {
      setFormData({
        ...formData,
        address: details.address,
        lat: details.lat,
        long: details.lng
      })
    } else {
      setFormData({
        ...formData,
        address: prediction.description,
        lat: null,
        long: null
      })
    }
    
    setShowSuggestions(false)
    setLocationSuggestions([])
  }

  const handleAddressChange = (value: string) => {
    setFormData({ ...formData, address: value, lat: null, long: null })
    
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current)
    }

    if (value.length >= 3) {
      searchTimeoutRef.current = window.setTimeout(() => {
        handleAddressSearch(value)
      }, 500)
    } else {
      setLocationSuggestions([])
      setShowSuggestions(false)
    }
  }

  const handleSendEmailOTP = async () => {
    if (!formData.businessEmail || !validators.email(formData.businessEmail)) {
      setErrors({ ...errors, businessEmail: 'Please enter a valid email' })
      return
    }

    setVerifyingEmail(true)
    setEmailError('')
    
    try {
      await businessService.sendOTP(formData.businessEmail)
      setEmailOtpSent(true)
      setEmailCountdown(60)
    } catch (err: any) {
      setEmailError(err.message || 'Failed to send OTP')
    } finally {
      setVerifyingEmail(false)
    }
  }

  const handleVerifyEmailOTP = async () => {
    if (emailOtp.length !== 6) {
      setEmailError('Please enter a 6-digit OTP')
      return
    }

    setVerifyingEmail(true)
    setEmailError('')

    try {
      const isValid = await businessService.verifyOTP(formData.businessEmail, emailOtp)
      
      if (isValid) {
        setEmailVerified(true)
        setEmailOtpSent(false)
        setEmailOtp('')
        setErrors(prev => ({ ...prev, businessEmail: '' }))
      } else {
        setEmailError('Invalid OTP. Please try again.')
      }
    } catch (err: any) {
      setEmailError(err.message || 'Verification failed')
    } finally {
      setVerifyingEmail(false)
    }
  }

  const handleSendPhoneOTP = async () => {
    if (!formData.phoneNumber || !isValidPhoneNumber(formData.phoneNumber)) {
      setErrors({ ...errors, phoneNumber: 'Please enter a valid phone number' })
      return
    }

    setVerifyingPhone(true)
    setPhoneError('')
    
    try {
      await businessService.sendOTP(formData.phoneNumber)
      setPhoneOtpSent(true)
      setPhoneCountdown(60)
    } catch (err: any) {
      setPhoneError(err.message || 'Failed to send OTP')
    } finally {
      setVerifyingPhone(false)
    }
  }

  const handleVerifyPhoneOTP = async () => {
    if (phoneOtp.length !== 6) {
      setPhoneError('Please enter a 6-digit OTP')
      return
    }

    setVerifyingPhone(true)
    setPhoneError('')

    try {
      const isValid = await businessService.verifyOTP(formData.phoneNumber, phoneOtp)
      
      if (isValid) {
        setPhoneVerified(true)
        setPhoneOtpSent(false)
        setPhoneOtp('')
        setErrors(prev => ({ ...prev, phoneNumber: '' }))
      } else {
        setPhoneError('Invalid OTP. Please try again.')
      }
    } catch (err: any) {
      setPhoneError(err.message || 'Verification failed')
    } finally {
      setVerifyingPhone(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      await onSubmit({
        businessName: formData.businessName.trim(),
        businessEmail: formData.businessEmail.trim().toLowerCase(),
        contactPerson: formData.contactPerson.trim(),
        phoneNumber: formData.phoneNumber,
        businessType: formData.businessType,
        website: formData.website.trim() || undefined,
        address: {
          address: formData.address.trim(),
          lat: formData.lat,
          long: formData.long
        }
      })
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Failed to save business' })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {errors.general}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <p className="font-medium mb-1">Required Fields</p>
        <p>All fields marked with * are mandatory and must be verified.</p>
      </div>

      <Input
        label="Business Name *"
        value={formData.businessName}
        onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
        error={errors.businessName}
        disabled={loading}
        maxLength={100}
        placeholder="Enter your business name"
      />

      <div>
        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <Input
              label="Business Email *"
              type="email"
              value={formData.businessEmail}
              onChange={(e) => {
                setFormData({ ...formData, businessEmail: e.target.value })
                setEmailVerified(false)
                setEmailOtpSent(false)
              }}
              error={errors.businessEmail}
              disabled={loading || emailVerified}
              placeholder="business@example.com"
            />
          </div>
          {!emailVerified && !emailOtpSent && (
            <Button
              type="button"
              onClick={handleSendEmailOTP}
              disabled={loading || !formData.businessEmail || verifyingEmail}
              size="md"
              variant="primary"
              loading={verifyingEmail}
            >
              Send OTP
            </Button>
          )}
        </div>

        {emailOtpSent && !emailVerified && (
          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <p className="text-sm text-blue-800">Enter the 6-digit code sent to {formData.businessEmail}</p>
            <input
              type="text"
              maxLength={6}
              value={emailOtp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                setEmailOtp(value)
                setEmailError('')
              }}
              placeholder="000000"
              className="w-full px-4 py-2 text-center text-xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            {emailError && (
              <div className="flex items-start space-x-2 text-sm text-red-600">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{emailError}</p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button
                type="button"
                onClick={handleVerifyEmailOTP}
                disabled={emailOtp.length !== 6 || verifyingEmail}
                loading={verifyingEmail}
                className="flex-1"
              >
                Verify
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSendEmailOTP}
                disabled={emailCountdown > 0 || verifyingEmail}
                className="flex-1"
              >
                {emailCountdown > 0 ? `Resend (${emailCountdown}s)` : 'Resend'}
              </Button>
            </div>
          </div>
        )}

        {emailVerified && (
          <p className="text-sm text-green-600 mt-1">✓ Email verified</p>
        )}
      </div>

      <Input
        label="Contact Person *"
        value={formData.contactPerson}
        onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
        error={errors.contactPerson}
        disabled={loading}
        placeholder="Full name of contact person"
      />

      <div>
        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">
              Phone Number *
            </label>
            <PhoneInput
              international
              defaultCountry="IN"
              value={formData.phoneNumber}
              onChange={(value) => {
                setFormData({ ...formData, phoneNumber: value || '' })
                setPhoneVerified(false)
                setPhoneOtpSent(false)
              }}
              disabled={loading || phoneVerified}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.phoneNumber && (
              <p className="text-sm text-red-600 mt-1">{errors.phoneNumber}</p>
            )}
          </div>
          {!phoneVerified && !phoneOtpSent && (
            <Button
              type="button"
              onClick={handleSendPhoneOTP}
              disabled={loading || !formData.phoneNumber || verifyingPhone}
              size="md"
              variant="primary"
              loading={verifyingPhone}
            >
              Send OTP
            </Button>
          )}
        </div>

        {phoneOtpSent && !phoneVerified && (
          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <p className="text-sm text-blue-800">Enter the 6-digit code sent to {formData.phoneNumber}</p>
            <input
              type="text"
              maxLength={6}
              value={phoneOtp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                setPhoneOtp(value)
                setPhoneError('')
              }}
              placeholder="000000"
              className="w-full px-4 py-2 text-center text-xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            {phoneError && (
              <div className="flex items-start space-x-2 text-sm text-red-600">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{phoneError}</p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button
                type="button"
                onClick={handleVerifyPhoneOTP}
                disabled={phoneOtp.length !== 6 || verifyingPhone}
                loading={verifyingPhone}
                className="flex-1"
              >
                Verify
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSendPhoneOTP}
                disabled={phoneCountdown > 0 || verifyingPhone}
                className="flex-1"
              >
                {phoneCountdown > 0 ? `Resend (${phoneCountdown}s)` : 'Resend'}
              </Button>
            </div>
          </div>
        )}

        {phoneVerified && (
          <p className="text-sm text-green-600 mt-1">✓ Phone verified</p>
        )}
      </div>

      <Select
        label="Business Type *"
        value={formData.businessType}
        onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
        options={[
          { value: '', label: 'Select business type' },
          ...OPTIONS.businessTypes.map(t => ({ value: t, label: t }))
        ]}
        error={errors.businessType}
        disabled={loading}
      />

      <div className="relative" ref={suggestionsRef}>
        <label className="block text-sm font-medium mb-1">
          Business Address * (Google Places)
        </label>
        <div className="relative">
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleAddressChange(e.target.value)}
            disabled={loading}
            placeholder="Start typing address (min 3 characters)..."
            className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.address ? 'border-red-500' : 'border-gray-300'
            }`}
            autoComplete="off"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {searchingLocation ? (
              <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            ) : formData.lat && formData.long ? (
              <MapPin className="w-4 h-4 text-green-600" />
            ) : (
              <Search className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
        
        {showSuggestions && locationSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {locationSuggestions.map((prediction, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleAddressSelect(prediction)}
                className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors focus:bg-blue-50 focus:outline-none"
              >
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {prediction.structured_formatting?.main_text || prediction.description.split(',')[0]}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {prediction.structured_formatting?.secondary_text || prediction.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {showSuggestions && locationSuggestions.length === 0 && !searchingLocation && formData.address.length >= 3 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
            <p className="text-sm text-gray-500 text-center">No locations found. Try a different search term.</p>
          </div>
        )}

        {errors.address && (
          <p className="text-sm text-red-600 mt-1">{errors.address}</p>
        )}
        
        {formData.lat && formData.long && (
          <div className="mt-2 p-2 bg-green-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-xs text-green-700 truncate">
                Location verified: {formData.lat.toFixed(4)}, {formData.long.toFixed(4)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, lat: null, long: null })}
              className="ml-2 p-1 hover:bg-green-100 rounded"
              title="Clear location"
            >
              <X className="w-3 h-3 text-green-600" />
            </button>
          </div>
        )}
      </div>

      <Input
        label="Website (Optional)"
        type="url"
        value={formData.website}
        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
        error={errors.website}
        disabled={loading}
        placeholder="https://www.example.com"
      />

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {business ? 'Update Business' : 'Create Business'}
        </Button>
      </div>
    </form>
  )
}